import { useState, useEffect } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Share2, Mail, AtSign } from "lucide-react"
import EmailTargetSelector, { type TargetSelection } from "./email-target-selector"
import SendToSelfDialog from "./send-to-self-dialog"
import api from "@crm-src/lib/api"
import useAuth from "@crm-src/hooks/useAuth"
import { sendSharedDocument } from "@crm-src/lib/shareDocument"
import {
  shareDocumentCompanyName,
  shareDocumentDefaults,
} from "@crm-src/lib/shareDocumentDefaults"

interface ShareDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  documentId: string | null
  documentType: 'invoice' | 'quote'
  documentNumber?: string
}

export function ShareDocumentModal({
  isOpen,
  onClose,
  documentId,
  documentType,
  documentNumber
}: ShareDocumentModalProps) {
  const { toast } = useToast()
  const { user } = useAuth()
  const [isSending, setIsSending] = useState(false)
  const [emailBody, setEmailBody] = useState('')
  const [subject, setSubject] = useState('')
  const [targetSelection, setTargetSelection] = useState<TargetSelection>({ recipients: [] })
  const [selfDialogOpen, setSelfDialogOpen] = useState(false)

  // Reset form when modal opens.
  //  • Subject defaults to "XOP Networks Invoice <ship-to company> <number>".
  //    The company isn't known synchronously here, so we set the base subject
  //    immediately, then fetch the record and fold the company in — but only
  //    if the user hasn't already edited the field (race-safe via the
  //    functional setState). See shareDocumentDefaults.
  //  • Message defaults to EMPTY — the share email is minimal ("<sharer> shared
  //    this invoice with you" + the PDF); any text here is an optional note.
  useEffect(() => {
    if (!isOpen || !documentId) return

    const base = shareDocumentDefaults(documentType, documentNumber)
    setSubject(base.subject)
    setEmailBody(base.body)
    setTargetSelection({ recipients: [] })

    let cancelled = false
    api
      .get(`/invoices/${documentId}`)
      .then((res) => {
        if (cancelled) return
        const data = res.data?.data || res.data
        const company = shareDocumentCompanyName(data ?? {})
        if (!company) return
        const withCompany = shareDocumentDefaults(documentType, documentNumber, company).subject
        // Don't clobber a subject the user has already started editing.
        setSubject((prev) => (prev === base.subject ? withCompany : prev))
      })
      .catch(() => {
        /* Keep the base subject if the record can't be fetched. */
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, documentId, documentNumber, documentType])

  const handleShare = async () => {
    if (!documentId) return

    // Validate recipients
    if (targetSelection.recipients.length === 0) {
      toast({
        title: "No recipients",
        description: "Please select at least one recipient",
        variant: "destructive",
      })
      return
    }

    // Validate subject
    if (!subject.trim()) {
      toast({
        title: "No subject",
        description: "Please enter an email subject",
        variant: "destructive",
      })
      return
    }

    try {
      setIsSending(true)

      const result = await sendSharedDocument({
        documentId,
        recipients: targetSelection.recipients,
        subject,
        body: emailBody,
      })

      if (result.success) {
        toast({
          title: "Success",
          description: `${documentType === 'quote' ? 'Quote' : 'Invoice'} shared successfully with ${targetSelection.recipients.length} recipient(s)`,
        })
        onClose()
      } else {
        throw new Error(result.error || 'Failed to share document')
      }
    } catch (error: any) {
      console.error('Error sharing document:', error)

      const errorData = error.response?.data
      const errorMessage = errorData?.error || 'Failed to share document'

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsSending(false)
    }
  }

  const handleClose = () => {
    if (isSending) return
    onClose()
  }

  if (!isOpen || !documentId) return null

  const docLabel = documentType === 'quote' ? 'Quote' : 'Invoice'

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ sx: { maxHeight: "90vh" } }}>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              height: 40,
              width: 40,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <Share2 size={20} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
              Share {docLabel}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              {documentNumber && `${docLabel} #${documentNumber}`}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, py: 2 }}>
          {/* Recipients Selection — its own bordered panel */}
          <Box
            data-testid="share-recipients-panel"
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              p: 2.5,
              bgcolor: "background.paper",
            }}
          >
            <Typography component="label" htmlFor="recipients" variant="body2" sx={{ fontWeight: 500, color: "text.secondary", mb: 1, display: "block" }}>
              Recipients *
            </Typography>
            <EmailTargetSelector
              value={targetSelection}
              onChange={setTargetSelection}
              disabled={isSending}
            />
            {targetSelection.recipients.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                {targetSelection.recipients.length} recipient(s) selected
              </Typography>
            )}
          </Box>

          {/* Email content — subject + message in a second bordered panel */}
          <Box
            data-testid="share-email-content-panel"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              p: 2.5,
              bgcolor: "background.paper",
            }}
          >
            {/* Subject */}
            <Box>
              <Typography component="label" htmlFor="subject" variant="body2" sx={{ fontWeight: 500, color: "text.secondary", mb: 1, display: "block" }}>
                Email Subject *
              </Typography>
              <TextField
                id="subject"
                type="text"
                size="small"
                fullWidth
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={`${docLabel} ${documentNumber || ''}`}
                disabled={isSending}
              />
            </Box>

            {/* Email Body */}
            <Box>
              <Typography component="label" htmlFor="emailBody" variant="body2" sx={{ fontWeight: 500, color: "text.secondary", mb: 1, display: "block" }}>
                Message (optional)
              </Typography>
              <TextField
                id="emailBody"
                multiline
                minRows={4}
                fullWidth
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Add an optional note — leave blank to keep it minimal"
                disabled={isSending}
              />
              <Typography variant="caption" sx={{ mt: 0.5, color: "text.secondary", display: "block" }}>
                The email is kept minimal — it just says you shared this {docLabel.toLowerCase()},
                with the PDF attached. Any note you add appears below that line.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1, justifyContent: "space-between" }}>
        {/* "Send to myself" — the invoice/quote analogue of the email-campaign
            Send Test button. Sends a self-copy to your own inbox, independent of
            the recipients chosen above (so it works even before you pick any). */}
        <Button
          variant="text"
          color="primary"
          onClick={() => setSelfDialogOpen(true)}
          disabled={isSending || !subject.trim()}
          startIcon={<AtSign size={16} />}
        >
          Send to myself
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleShare}
            disabled={isSending || targetSelection.recipients.length === 0 || !subject.trim()}
            startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <Mail size={16} />}
          >
            {isSending ? "Sending..." : `Send ${docLabel}`}
          </Button>
        </Box>
      </DialogActions>

      <SendToSelfDialog
        open={selfDialogOpen}
        onOpenChange={setSelfDialogOpen}
        documentId={documentId}
        documentType={documentType}
        subject={subject}
        body={emailBody}
        defaultEmail={user?.email ?? ""}
      />
    </Dialog>
  )
}
